// Tarjeta de un modelo en «Elige el teléfono»: ficha y chips de almacenamiento, RAM y color, y el
// precio, promos y crédito de la variante elegida. «Elegir» aparta el SKU de esa variante.
import { ArrowRight, Check, Smartphone } from 'lucide-react';
import { Button } from '@/kit';
import { cn } from '@/lib/cn';
import { VariantPicker } from '@/shared/patterns/VariantPicker';
import {
    variantCountLabel,
    variantSelector,
    type ModelGroup,
} from '@/domains/sales/portal/catalog/lib/modelGroups';
import type { LevelCreditTerms } from '../cardBenefits';
import { isSoldOut } from '../catalogAvailability';
import type { BenefitBubble, CardSaleBenefits } from '../promotionSlots';
import type { CatalogItem } from '../types';
import { HighlightedText } from './HighlightedText';
import { StockChip } from './StockChip';
import { CardCreditSection, CardPrice, CardPromosSection, cardPriceOf } from './CardPricing';

const NO_SALE_BENEFITS: CardSaleBenefits = { bubbles: [], discountedPriceCents: null };
const NO_TERMS: LevelCreditTerms = {};

export function ModelCard({
    group,
    variant,
    selected,
    terms,
    busy = false,
    onSelect,
    onVariantChange,
    onChoose,
    saleBenefits = NO_SALE_BENEFITS,
    creditTerms = NO_TERMS,
    creditBubbles = [],
}: {
    group: ModelGroup;
    /** La variante que muestra la tarjeta. */
    variant: CatalogItem;
    /** La tarjeta es la del detalle. */
    selected: boolean;
    /** Términos de la búsqueda, para resaltar la coincidencia. */
    terms: string[];
    busy?: boolean;
    /** Tocar el nombre muestra el detalle de la variante, sin apartar. */
    onSelect: (item: CatalogItem) => void;
    onVariantChange: (item: CatalogItem) => void;
    onChoose: (item: CatalogItem) => void;
    /** Burbujas propias y precio con descuento de la variante. */
    saleBenefits?: CardSaleBenefits;
    creditTerms?: LevelCreditTerms;
    creditBubbles?: readonly BenefitBubble[];
}) {
    const selector = variantSelector(group, variant);
    const variantSoldOut = isSoldOut(variant);
    const { priceCents } = cardPriceOf(variant.cashPriceCents, saleBenefits);
    const bubbles = [...saleBenefits.bubbles, ...creditBubbles];

    return (
        <article
            aria-label={`${group.brand} ${group.model}`}
            className={cn(
                'relative flex min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card transition-all',
                selected
                    ? 'border-foreground shadow-[0_0_0_1px_hsl(var(--foreground))]'
                    : 'border-border',
                group.soldOut && 'opacity-[0.52]',
            )}
        >
            <div className="flex min-w-0 flex-1 flex-col gap-0.5 px-4 pb-3.5 pt-4">
                <span className="flex min-h-5 items-center gap-1.5 pr-6 text-muted-foreground">
                    <Smartphone className="size-4 flex-none" aria-hidden />
                    <span className="min-w-0 truncate text-[11px] font-bold uppercase tracking-[0.06em]">
                        <HighlightedText text={group.brand} terms={terms} /> · Teléfono
                    </span>
                    {group.variants.length > 1 && (
                        <span className="ml-auto flex-none text-[11px]">
                            {variantCountLabel(group)}
                        </span>
                    )}
                </span>
                {selected && (
                    <span className="absolute right-3 top-3 flex size-[22px] items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Check className="size-3" aria-hidden />
                    </span>
                )}
                <span className="mt-1 flex min-w-0 items-center gap-1.5">
                    <button
                        type="button"
                        aria-pressed={selected}
                        disabled={group.soldOut}
                        onClick={() => {
                            onSelect(variant);
                        }}
                        title={group.model}
                        className="min-w-0 truncate rounded-sm text-left text-[16px] font-bold leading-tight hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:no-underline"
                    >
                        <HighlightedText text={group.model} terms={terms} />
                    </button>
                    <StockChip item={variant} className="flex-none whitespace-nowrap" />
                </span>

                <div className="mt-0.5">
                    <VariantPicker
                        summary={selector.summary}
                        dimensions={selector.dimensions}
                        onSelect={(sku) => {
                            const next = group.variants.find((item) => item.sku === sku);
                            if (next) onVariantChange(next);
                        }}
                    />
                </div>

                <CardPrice
                    listCents={variant.cashPriceCents}
                    saleBenefits={saleBenefits}
                    note={`SKU ${variant.sku}`}
                />
            </div>

            <CardCreditSection item={variant} priceCents={priceCents} creditTerms={creditTerms} />
            <CardPromosSection
                bubbles={bubbles}
                action={
                    <Button
                        size="sm"
                        disabled={variantSoldOut || busy}
                        rightIcon={<ArrowRight className="size-4" aria-hidden />}
                        onClick={() => {
                            onChoose(variant);
                        }}
                    >
                        {variantSoldOut ? 'Sin piezas' : 'Elegir'}
                    </Button>
                }
            />
        </article>
    );
}
